import React, { memo, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { Text, useTheme } from '@Sage/hoi-poi-ui';
import { getMomentFromDateBackend, formatDateWithTimeOrNot } from 'utils/dates';
import { formatDecimalByConfig, DECIMAL_TYPES } from 'utils/decimal';
import { getLiteral } from 'utils/getLiteral';
import { ACTIVITIES } from 'constants/Entities';

import { getBackendBoolean } from '@Sage/ssm-utils/backendScalars';
import { ExtraFieldsLoader } from '../Loaders';

import TextWithLinks from 'components/TextWithLinks';

import './styles.scss';

const mapStateToProps = (state) => {
    const extraFields = state.activities?.extraFields || null;

    // Get activity data for decimal configuration
    const entityDetail = state?.entityDetail || null;
    const tabsObjEntris = Object.entries(state.entityDetail?.tabs || {});
    const primaryTab = tabsObjEntris.find(([key, value]) => value.primary)?.[0];
    const activeDetailKey = tabsObjEntris.length > 1 ? primaryTab : entityDetail?.active;
    const activeDetail = entityDetail?.[activeDetailKey] || null;
    const activity = activeDetail?.data && activeDetail?.data?.Id ? activeDetail.data : null;

    return {
        loading: extraFields?.loading || false,
        extraFields: extraFields?.data || [],
        activity: activity,
    };
};

const DetailExtraFields = memo(({ loading, extraFields, width, height, activity }) => {
    const theme = useTheme();

    const renderRows = useCallback(
        (list) => {
            return list
                .filter((item) => !getBackendBoolean(item.hiddenByDynamism))
                .map((current, index) => {
                    return (
                        <div key={index} className="fm-activity-detail-extrafields__row">
                            <div className="fm-activity-detail-extrafields__row-field">
                                <Text color={theme.colors.utility.textSecondary}>
                                    {current.Description}
                                </Text>
                            </div>
                            <div className="fm-activity-detail-extrafields__row-value">
                                <Text>{current.value}</Text>
                            </div>
                        </div>
                    );
                });
        },
        [theme],
    );

    const renderSection = useCallback(
        (section, key) => {
            section.list.map((field) => {
                let value;
                let dataType;

                switch (field.DataType) {
                    case '0':
                        dataType = 'text';
                        break;
                    case '1':
                        dataType = 'text';
                        break;
                    case '2':
                        dataType = 'decimal';
                        break;
                    case '3':
                        dataType = 'date';
                        break;
                    case '4':
                        dataType = 'bool';
                        break;
                    case '5':
                        dataType = 'currency';
                        break;
                    case '6':
                        dataType = 'percent';
                        break;
                    default:
                        dataType = 'text';
                        break;
                }

                if (field.Value || typeof field.Value === 'boolean') {
                    value = field.Value;
                    switch (dataType) {
                        case 'text':
                            value = <TextWithLinks text={value} />;
                            break;
                        case 'decimal':
                            value = formatDecimalByConfig({
                                value: field.Value,
                                type: DECIMAL_TYPES.GENERAL,
                                idCurrency: activity?.IdCurrency,
                                entityName: ACTIVITIES.extraFieldName,
                                fieldId: field.Id,
                            });
                            break;
                        case 'percent':
                            value = field.Value
                                ? `${formatDecimalByConfig({
                                      value: field.Value,
                                      type: DECIMAL_TYPES.GENERAL,
                                      idCurrency: activity?.IdCurrency,
                                      entityName: ACTIVITIES.extraFieldName,
                                      fieldId: field.Id,
                                  })}%`
                                : '-';
                            break;
                        case 'relatedValueList':
                        case 'singleValueList':
                        case 'fuzzySearchSingle':
                            value = field.label || field.Label;
                            break;
                        case 'bool':
                            value = getBackendBoolean(field.Value)
                                ? getLiteral('common_yes')
                                : getLiteral('common_no');
                            break;
                        case 'currency':
                            value = formatDecimalByConfig({
                                value: field.Value,
                                type: DECIMAL_TYPES.CURRENCY_GENERAL,
                                idCurrency: activity?.IdCurrency || activity?.idCurrency,
                                currencySymbol: activity?.SymbolCurrency,
                                entityName: ACTIVITIES.extraFieldName,
                                fieldId: field.Id,
                            });
                            break;
                        case 'date':
                            const includeTime = getBackendBoolean(field.includeTime);
                            value = getMomentFromDateBackend(value);
                            value = formatDateWithTimeOrNot(value, includeTime);
                            break;
                    }
                }
                field.value = value;
            });

            if (!section.list?.find((item) => !getBackendBoolean(item.hiddenByDynamism)))
                return null;

            return (
                <div key={key} className="fm-activity-detail-extrafields__section">
                    <div className="fm-activity-detail-extrafields__header">
                        <Text bold={true}>{section.description}</Text>
                    </div>
                    {renderRows(section.list)}
                </div>
            );
        },
        [renderRows, activity],
    );

    const renderList = useMemo(() => {
        if (!extraFields?.length) return null;
        return extraFields.reduce((arr, current, index) => {
            if (!current?.list?.length) return arr;
            arr.push(renderSection(current, index));
            return arr;
        }, []);
    }, [renderSection, extraFields]);

    return (
        <div className="fm-activity-detail-extrafields">
            {loading && <ExtraFieldsLoader width={width} height={height} />}
            {!loading && <div className="fm-activity-detail-extrafields__list">{renderList}</div>}
        </div>
    );
});

export default connect(mapStateToProps)(DetailExtraFields);
